import React, { useState, useEffect, useCallback } from 'react';
import { useTon } from './TelegramTonProvider';
import { useTelegram } from './TelegramProvider';
import { useLilapeBalance } from './hooks/useLilapeBalance';
import { PRIZE_POOL_ADDRESS, LILAPE_JETTON_ADDRESS } from './config';
import { buildJettonTransferPayload, getUserJettonWallet } from './jettonTransfer';
import { formatLilape } from './config/gameConfig';

const PERIODS = [
  { days: 90, label: '90 Days', apy: 20, accent: '#2563EB' },
  { days: 180, label: '180 Days', apy: 30, accent: '#7C3AED' },
  { days: 360, label: '360 Days', apy: 50, accent: '#F59E0B' },
];

const MIN_STAKE = 1_000_000_000_000_000_000n; // 1B tokens (1 billion * 10^9 decimals)
const SECONDS_PER_YEAR = 365 * 24 * 60 * 60;

const calcReward = (amount, apy, days) => {
  return BigInt(Math.floor((Number(amount) * apy * days * 86400) / (100 * SECONDS_PER_YEAR)));
};

const TonStakingPage = () => {
  const { connected, wallet, sendTransaction, address } = useTon();
  const { showAlert, hapticFeedback } = useTelegram();
  const { balance: lilapeBalance, balanceRaw: lilapeBalanceRaw, refetch: refetchBalance } = useLilapeBalance(connected ? address : null);

  const [selectedPeriod, setSelectedPeriod] = useState(PERIODS[0]);
  const [amount, setAmount] = useState('1000000000000000000'); // 1B tokens
  const [stakes, setStakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStakes, setLoadingStakes] = useState(false);
  const [tab, setTab] = useState('new');
  const [status, setStatus] = useState('');

  // Fetch user stakes from API
  const fetchStakes = useCallback(async () => {
    if (!address) return;
    setLoadingStakes(true);
    try {
      const res = await fetch(`/.netlify/functions/staking?action=info&address=${address}`);
      const data = await res.json();
      if (data.stakes) {
        setStakes(data.stakes);
      }
    } catch (err) {
      console.error('Failed to fetch stakes:', err);
    } finally {
      setLoadingStakes(false);
    }
  }, [address]);

  useEffect(() => {
    fetchStakes();
  }, [fetchStakes]);

  // Stake LILAPE
  const handleStake = async () => {
    if (!connected || !wallet) {
      showAlert('Connect your TON wallet first');
      return;
    }

    const amt = BigInt(amount);
    if (amt < BigInt(MIN_STAKE)) {
      showAlert('Minimum stake: 1 LILAPE');
      return;
    }

    if (lilapeBalanceRaw && amt > BigInt(lilapeBalanceRaw)) {
      showAlert('Insufficient LILAPE balance');
      return;
    }

    setLoading(true);
    setStatus('Preparing transaction...');
    hapticFeedback?.('light');

    try {
      // Get user's jetton wallet
      const userJettonWallet = await getUserJettonWallet(address, LILAPE_JETTON_ADDRESS);

      // Build transfer payload (lock days are registered via API, not in tx)
      const payload = await buildJettonTransferPayload(amt, PRIZE_POOL_ADDRESS);

      setStatus('Waiting for wallet approval...');

      const txResult = await sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [{
          address: userJettonWallet,
          amount: String(50000000), // 0.05 TON for gas
          payload,
        }],
      });

      if (txResult?.boc) {
        setStatus('Confirming on-chain... (5-10s)');
        await new Promise(r => setTimeout(r, 8000));

        // Register stake with API
        setStatus('Registering stake...');
        const res = await fetch('/.netlify/functions/staking?action=stake', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address,
            amount: amt.toString(),
            lockDays: selectedPeriod.days,
            txHash: txResult.boc.slice(0, 64),
          }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to register stake');

        hapticFeedback?.('heavy');
        setStatus('');
        setTab('active');
        showAlert(`✅ Staked ${formatLilape(Number(amt))} LILAPE for ${selectedPeriod.days} days!\nExpected reward: ${formatLilape(Number(data.expectedReward))} LILAPE`);
        fetchStakes();
        refetchBalance?.();
      } else {
        // Transaction was sent but no BOC returned (cancelled or failed silently)
        setStatus('');
      }
    } catch (err) {
      console.error('Staking error:', err);
      if (err.message?.includes('reject') || err.message?.includes('cancel')) {
        setStatus('');
      } else if (err.message?.includes('WebAppMethodUnsupported')) {
        setStatus('Error: Please try again or reconnect wallet');
      } else {
        setStatus(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Unstake
  const handleUnstake = async (stake) => {
    if (!connected) {
      showAlert('Connect wallet first');
      return;
    }

    setLoading(true);
    setStatus('Processing unstake...');
    hapticFeedback?.('light');

    try {
      const res = await fetch('/.netlify/functions/staking?action=unstake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address,
          stakeId: stake.id,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to unstake');

      hapticFeedback?.('heavy');
      setStatus('');
      showAlert(`✅ Unstaked!\nAmount: ${formatLilape(Number(data.amountReturned))} LILAPE\nRewards: ${formatLilape(Number(data.rewardsPaid))} LILAPE`);
      fetchStakes();
      refetchBalance?.();
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Claim rewards
  const handleClaim = async (stake) => {
    if (!connected) {
      showAlert('Connect wallet first');
      return;
    }

    setLoading(true);
    setStatus('Claiming rewards...');
    hapticFeedback?.('light');

    try {
      const res = await fetch('/.netlify/functions/staking?action=claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address,
          stakeId: stake.id,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to claim');

      hapticFeedback?.('heavy');
      setStatus('');
      showAlert(`✅ Claimed ${formatLilape(Number(data.rewardsClaimed))} LILAPE rewards!`);
      fetchStakes();
      refetchBalance?.();
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const amtBigInt = BigInt(amount || '0');
  const reward = calcReward(amtBigInt, selectedPeriod.apy, selectedPeriod.days);
  const activeStakes = stakes.filter(s => s.active);

  // Preset amounts in REAL tokens (nano = tokens * 10^9)
  // 1B tokens = 1,000,000,000 * 10^9 nano = 1e18
  const presets = [
    { label: '1B', value: '1000000000000000000' },      // 1 billion tokens
    { label: '10B', value: '10000000000000000000' },    // 10 billion tokens
    { label: '50B', value: '50000000000000000000' },    // 50 billion tokens
    { label: 'MAX', value: lilapeBalanceRaw || '0', isMax: true },
  ];

  return (
    <div style={{ padding: '0 0 96px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon green">🏦</div>
        <div style={{ flex: 1 }}>
          <div className="page-title">Stake LILAPE</div>
          <div className="page-subtitle">Lock tokens — earn {selectedPeriod.apy}% APY</div>
        </div>
        {connected && lilapeBalance !== null && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Balance</div>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--gold)' }}>{formatLilape(lilapeBalance)} LILAPE</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', margin: '0 16px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 4, gap: 4 }}>
        {['new', 'active'].map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); if (t === 'new') { setLoading(false); setStatus(''); } }}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              transition: 'all 0.2s ease',
              background: tab === t ? 'var(--gold)' : 'transparent',
              color: tab === t ? '#000' : 'var(--text-muted)',
            }}
          >
            {t === 'new' ? '＋ New Stake' : `Active (${activeStakes.length})`}
          </button>
        ))}
      </div>

      {/* Status */}
      {status && (
        <div style={{
          margin: '0 16px 12px',
          padding: '12px',
          borderRadius: 10,
          background: status.startsWith('Error') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
          color: status.startsWith('Error') ? '#EF4444' : '#10B981',
          fontSize: 13,
          textAlign: 'center',
        }}>
          {status}
        </div>
      )}

      {tab === 'new' && (
        <div>
          {/* Period selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, padding: '0 16px', marginBottom: 16 }}>
            {PERIODS.map(p => {
              const active = selectedPeriod.days === p.days;
              return (
                <button
                  key={p.days}
                  onClick={() => setSelectedPeriod(p)}
                  disabled={loading}
                  style={{
                    padding: '14px 8px',
                    borderRadius: 'var(--radius-md)',
                    border: `2px solid ${active ? p.accent : 'var(--border)'}`,
                    background: active ? `${p.accent}22` : 'var(--bg-card)',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    transform: active ? 'scale(1.04)' : 'scale(1)',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{p.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: p.accent }}>{p.apy}%</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>APY</div>
                </button>
              );
            })}
          </div>

          {/* Amount presets */}
          <div style={{ display: 'flex', gap: 8, padding: '0 16px', marginBottom: 12 }}>
            {presets.map(p => (
              <button
                key={p.label}
                onClick={() => setAmount(p.value)}
                disabled={loading || (p.isMax && !lilapeBalance)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: 8,
                  border: amount === p.value
                    ? '2px solid var(--gold)'
                    : p.isMax
                      ? '1px solid #10B981'
                      : '1px solid var(--border)',
                  background: amount === p.value
                    ? 'rgba(245,158,11,0.1)'
                    : p.isMax
                      ? 'rgba(16,185,129,0.1)'
                      : 'var(--bg-card)',
                  color: amount === p.value
                    ? 'var(--gold)'
                    : p.isMax
                      ? '#10B981'
                      : 'var(--text-secondary)',
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Reward preview */}
          {amtBigInt >= BigInt(MIN_STAKE) && (
            <div style={{
              margin: '0 16px 16px',
              padding: 18,
              borderRadius: 'var(--radius-lg)',
              background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(16,185,129,0.08))',
              border: '1px solid rgba(245,158,11,0.25)',
            }}>
              {[
                { label: 'You stake', value: `${formatLilape(Number(amtBigInt))} LILAPE`, color: 'var(--text-primary)' },
                { label: 'Lock period', value: `${selectedPeriod.days} days`, color: 'var(--text-primary)' },
                { label: 'APY', value: `${selectedPeriod.apy}%`, color: 'var(--gold)' },
                { label: 'Expected reward', value: `+${formatLilape(Number(reward))} LILAPE`, color: 'var(--green)' },
                { label: 'Total return', value: `${formatLilape(Number(amtBigInt + reward))} LILAPE`, color: 'var(--text-primary)', bold: true },
              ].map((row, i, arr) => (
                <div key={row.label} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{row.label}</span>
                  <span style={{ fontSize: 14, fontWeight: row.bold ? 700 : 600, color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Stake button */}
          <div style={{ padding: '0 16px' }}>
            <button
              onClick={handleStake}
              disabled={!connected || loading || amtBigInt < BigInt(MIN_STAKE)}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: (!connected || loading || amtBigInt < BigInt(MIN_STAKE)) ? '#444' : 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: '#fff',
                fontSize: '16px',
                fontWeight: '700',
                cursor: (!connected || loading || amtBigInt < BigInt(MIN_STAKE)) ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {!connected ? '🔗 Connect Wallet First' :
                loading ? '⏳ Processing…' :
                  `🏦 Stake ${formatLilape(Number(amtBigInt))} LILAPE`}
            </button>
          </div>

          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 12, padding: '0 16px' }}>
            LILAPE staking · {selectedPeriod.apy}% APY · Rewards paid on unlock or claim
          </p>
        </div>
      )}

      {tab === 'active' && (
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loadingStakes ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              Loading stakes...
            </div>
          ) : activeStakes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>🏦</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>No active stakes yet</div>
              <button
                onClick={() => setTab('new')}
                className="btn-primary"
                style={{ width: 'auto', padding: '12px 24px', display: 'inline-flex' }}
              >
                ＋ Create Stake
              </button>
            </div>
          ) : activeStakes.map(stake => {
            const now = Math.floor(Date.now() / 1000);
            const progress = Math.min(100, ((now - stake.startTime) / (stake.endTime - stake.startTime)) * 100);
            const daysLeft = Math.max(0, Math.ceil((stake.endTime - now) / 86400));
            const canUnstake = stake.canUnstake || now >= stake.endTime;
            const pendingRewards = BigInt(stake.pendingRewards || '0');
            const period = PERIODS.find(p => p.days === stake.lockDays) || PERIODS[0];

            return (
              <div key={stake.id} style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: 16,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{formatLilape(Number(stake.amount))} LILAPE</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{stake.lockDays} days · {period.apy}% APY</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)' }}>
                      +{formatLilape(Number(pendingRewards))} LILAPE
                    </div>
                    <span className="badge badge-green" style={{ marginTop: 4 }}>
                      Pending
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ background: 'var(--bg-secondary)', borderRadius: 99, height: 6, marginBottom: 6, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${period.accent}, var(--green))`,
                    borderRadius: 99,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
                  <span>{new Date(stake.startTime * 1000).toLocaleDateString()}</span>
                  <span>{canUnstake ? '✅ Unlocked' : `${daysLeft}d left`}</span>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  {pendingRewards > 0n && (
                    <button
                      onClick={() => handleClaim(stake)}
                      disabled={loading}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: 'none',
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        background: 'linear-gradient(135deg, #10B981, #059669)',
                        color: '#fff',
                        opacity: loading ? 0.5 : 1,
                      }}
                    >
                      💰 Claim Rewards
                    </button>
                  )}
                  <button
                    onClick={() => handleUnstake(stake)}
                    disabled={loading || !canUnstake}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: loading || !canUnstake ? 'not-allowed' : 'pointer',
                      background: canUnstake ? 'var(--gold)' : 'var(--bg-secondary)',
                      color: canUnstake ? '#000' : 'var(--text-muted)',
                      opacity: loading || !canUnstake ? 0.5 : 1,
                    }}
                  >
                    {canUnstake ? '🔓 Unstake' : `🔒 ${daysLeft}d`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export { TonStakingPage };
